import React from 'react';
import { ArrowRight, ShieldAlert } from 'lucide-react';
import { useIsMobile } from '../../../utils/isMobile';
import { LeakEntry } from '../../../types/interfaces';

interface Props {
  blurredLeaks: LeakEntry[];
  totalCount: number;
  t: (key: string) => string;
  onViewMore: () => void;
}

const ScanLeaksCards: React.FC<Props> = ({
  blurredLeaks,
  totalCount,
  t,
  onViewMore
}) => {
  const isMobile = useIsMobile();

  const containerPadding = isMobile ? 'p-4' : 'p-6';
  const titleSize        = isMobile ? 'text-lg' : 'text-xl';
  const iconSize         = isMobile ? 'w-12 h-12' : 'w-16 h-16';
  const gridCols         = isMobile ? 'grid-cols-1' : 'grid-cols-2';
  const cardPadding      = isMobile ? 'p-4' : 'p-5';
  const fieldKeySize     = isMobile ? 'text-sm' : 'text-base';
  const fieldValSize     = isMobile ? 'text-sm' : 'text-base';
  const helperSize       = isMobile ? 'text-sm' : 'text-lg';
  const btnPadding       = isMobile ? 'px-4 py-2' : 'px-6 py-2';
  const btnTextSize      = isMobile ? 'text-base' : 'text-lg';
  const btnIconSize      = isMobile ? 'w-4 h-4' : 'w-5 h-5';
  const remainingSize    = isMobile ? 'text-base' : 'text-lg';

  const FIELDS = [
    { key: 'password', label: t('scan.password') },
    { key: 'hashed_password', label: t('scan.hashedPassword') },
    { key: 'username', label: t('scan.username') },
    { key: 'name', label: t('scan.name') },
    { key: 'first_name', label: t('scan.firstName') },
    { key: 'last_name', label: t('scan.lastName') },
    { key: 'dob', label: t('scan.dob') },
    { key: 'gender', label: t('scan.gender') },
    { key: 'phone', label: t('scan.phone') },
    { key: 'ip_address', label: t('scan.ipAddress') },
    { key: 'address', label: t('scan.address') },
    { key: 'city', label: t('scan.city') },
    { key: 'state', label: t('scan.state') },
    { key: 'province', label: t('scan.province') },
    { key: 'zip', label: t('scan.zip') },
    { key: 'country', label: t('scan.country') },
    { key: 'origin', label: t('scan.origin') },
  ] as const;

  // Detecta si el contenido tiene pocos campos visibles
  const isShortEntry = (entry: LeakEntry) => {
    let count = 0;
    for (const { key } of FIELDS) {
      if ((entry as any)[key]) count++;
    }
    return count < 4;
  };

  const remaining = totalCount > blurredLeaks.length
    ? totalCount - blurredLeaks.length
    : 0;

  const viewMoreBtn =
    'bg-gradient-to-r from-yellow-500 to-yellow-700 ' +
    'hover:from-yellow-600 hover:to-yellow-800 ' +
    `rounded-lg flex items-center gap-2 transition-all text-white shadow-md shadow-yellow-900 ${btnPadding} ${btnTextSize}`;

  return (
    <div
      className={`
        mt-8 bg-gray-900 bg-opacity-60 backdrop-blur-md 
        rounded-xl shadow-lg shadow-black/30 border border-gray-700
        ${containerPadding}
      `}
    >
      <h3 className={`font-semibold text-gray-200 mb-6 text-center ${titleSize}`}>
        {t('scan.leaksFound')}
      </h3>

      {blurredLeaks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8">
          <ShieldAlert className={`${iconSize} text-green-400 mb-4`} />
          <p className={`text-gray-300 font-medium ${helperSize}`}>
            {t('scan.noLeaksFound')}
          </p>
        </div>
      ) : (
        <>
          <div className={`grid ${gridCols} gap-6 items-stretch`}>
            {blurredLeaks.map((entry, i) => (
              <div
                key={i}
                className={`
                  bg-gray-800 rounded-lg shadow-md transition 
                  duration-200 hover:bg-gray-700 
                  ${cardPadding}
                  flex flex-col h-full
                `}
              >
                {/* Contenido principal centrado si es corto */}
                <div className={`flex-1 flex flex-col ${isShortEntry(entry) ? 'justify-center' : ''}`}>
                  {/* Email */}
                  <div className="mb-3">
                    <span className={`font-bold text-gray-300 mr-1 ${fieldKeySize}`}>
                      {t('scan.email')}:
                    </span>
                    <span className={`inline-block max-w-full overflow-hidden whitespace-nowrap text-ellipsis align-bottom ${fieldValSize} text-gray-200`}>
                      {entry.email}
                    </span>
                  </div>

                  {/* Campos extra */}
                  {FIELDS.map(({ key, label }) => {
                    const val = (entry as any)[key];
                    if (!val) return null;
                    return (
                      <div key={key} className="mb-2">
                        <span className={`font-bold text-gray-300 mr-1 ${fieldKeySize}`}>
                          {label}:
                        </span>
                        <span className={`inline-block max-w-[200px] overflow-hidden whitespace-nowrap text-ellipsis align-bottom ${fieldValSize} text-gray-200`}>
                          {val}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Footer abajo */}
                <div className="mt-4 pt-4 border-t border-gray-600">
                  {entry.database_name && (
                    <div className="mb-1">
                      <span className={`font-bold text-gray-300 mr-1 ${fieldKeySize}`}>
                        {t('scan.databaseName')}:
                      </span>
                      <span className={`text-gray-200 ${fieldValSize}`}>
                        {entry.database_name}
                      </span>
                    </div>
                  )}
                  {entry.source_name && (
                    <div className="mb-1">
                      <span className={`font-bold text-gray-300 mr-1 ${fieldKeySize}`}>
                        {t('scan.source')}:
                      </span>
                      <span className={`text-gray-200 ${fieldValSize}`}>
                        {entry.source_name}
                      </span>
                    </div>
                  )}
                  {entry.source_breach_date && (
                    <div>
                      <span className={`font-bold text-gray-300 mr-1 ${fieldKeySize}`}>
                        {t('scan.breachDate')}:
                      </span>
                      <span className={`text-gray-200 ${fieldValSize}`}>
                        {entry.source_breach_date}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {totalCount > 5 && (
            <div className="mt-6 flex flex-col items-center">
              {remaining > 0 && (
                <span className={`text-gray-400 font-semibold mb-4 ${remainingSize}`}>
                  {remaining} {t('scan.more')}
                </span>
              )}
              <button className={viewMoreBtn} onClick={onViewMore}>
                {t('scan.viewMore')}
                <ArrowRight className={`${btnIconSize}`} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ScanLeaksCards;
